import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';

const SpecificationsEditor = ({ projectId, onClose }) => {
    const [spec, setSpec] = useState(null); // The record
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form Fields
    const [meetingNotes, setMeetingNotes] = useState('');
    const [roadmap, setRoadmap] = useState([]); // [{id, category, task, done}]
    const [status, setStatus] = useState('draft');

    useEffect(() => {
        if (projectId) {
            loadSpec();
        }
    }, [projectId]);

    const loadSpec = async () => {
        setLoading(true);
        try {
            // Use getList because getFirstListItem throws 404 if not found, which is annoying to catch sometimes
            // actually getFirstListItem is fine if we catch it.
            const res = await pb.collection('specifications').getFirstListItem(`project="${projectId}"`);
            setSpec(res);
            setMeetingNotes(res.meeting_notes || '');
            setRoadmap(res.roadmap || []);
            setStatus(res.status || 'draft');
        } catch (e) {
            // If 404, we just stay with empty defaults, ready to create
            // For any other error, log it
            if (e.status !== 404) {
                console.error("Error loading spec:", e);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const data = {
            project: projectId,
            meeting_notes: meetingNotes,
            roadmap: roadmap,
            status: status
        };

        try {
            if (spec) {
                await pb.collection('specifications').update(spec.id, data);
            } else {
                const newSpec = await pb.collection('specifications').create(data);
                setSpec(newSpec);
            }
            onClose();
        } catch (e) {
            alert('Erreur lors de la sauvegarde: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    // Roadmap Helpers
    const addTask = () => {
        const newTask = { id: Date.now(), category: 'Général', task: '', done: false };
        setRoadmap([...roadmap, newTask]);
    };

    const updateTask = (id, field, value) => {
        setRoadmap(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
    };

    const removeTask = (id) => {
        setRoadmap(prev => prev.filter(t => t.id !== id));
    };

    if (loading) return <div style={{ padding: 20, color: '#64748b' }}>Chargement du cahier des charges...</div>;

    return (
        <div className="spec-editor" style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '85vh' }}>
            <div style={{ padding: '0 0 20px 0', borderBottom: '1px solid #var(--admin-border)', marginBottom: 20 }}>
                <h2 style={{ margin: '0 0 16px 0' }}>Cahier des Charges & Suivi</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={{ fontWeight: 500 }}>Statut du document :</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #cbd5e1' }}>
                        <option value="draft">Brouillon (Interne)</option>
                        <option value="review">En lecture client</option>
                        <option value="validated">Validé</option>
                    </select>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Left: Notes */}
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: '1.1rem' }}>📝 Notes de réunion / Besoins</h3>
                    <textarea
                        value={meetingNotes}
                        onChange={(e) => setMeetingNotes(e.target.value)}
                        style={{
                            flex: 1,
                            width: '100%',
                            padding: 16,
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            resize: 'none',
                            fontFamily: 'inherit',
                            lineHeight: 1.6,
                            outline: 'none'
                        }}
                        placeholder="Prenez vos notes ici pendant le RDV..."
                    />
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>Ce texte sera visible par le client si le statut n'est pas "Brouillon".</p>
                </div>

                {/* Right: Roadmap */}
                <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', paddingRight: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>🚀 Roadmap / Tâches</h3>
                        <button onClick={addTask} style={{ padding: '6px 12px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}>+ Ajouter une tâche</button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {roadmap.map((item) => (
                            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto auto', gap: 10, alignItems: 'center', background: '#f8fafc', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <input
                                    type="text"
                                    value={item.category}
                                    onChange={(e) => updateTask(item.id, 'category', e.target.value)}
                                    style={{ padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.85rem', width: '100%' }}
                                    placeholder="Catégorie"
                                />
                                <input
                                    type="text"
                                    value={item.task}
                                    onChange={(e) => updateTask(item.id, 'task', e.target.value)}
                                    style={{ padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.9rem', width: '100%' }}
                                    placeholder="Tâche à réaliser..."
                                />
                                <input
                                    type="checkbox"
                                    checked={item.done}
                                    onChange={(e) => updateTask(item.id, 'done', e.target.checked)}
                                    title="Marquer comme fait"
                                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                                />
                                <button onClick={() => removeTask(item.id)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0 4px', lineHeight: 1 }} title="Supprimer">×</button>
                            </div>
                        ))}
                        {roadmap.length === 0 && (
                            <div style={{ textAlign: 'center', padding: 30, border: '2px dashed #e2e8f0', borderRadius: 8, color: '#94a3b8' }}>
                                Aucune tâche définie pour le moment.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button onClick={onClose} style={{ padding: '10px 20px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Annuler</button>
                <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
};

export default SpecificationsEditor;
